import React, { useEffect, useState } from 'react';
import API from 'shared/api';
import {
  FiAlertTriangle,
  FiBarChart2,
  FiDownload,
  FiList,
  FiClock,
  FiSearch,
} from 'react-icons/fi';
import { COL } from 'shared/darkColors';
import { LineChart, BarsH } from 'shared/components/Dashboard/charts';
import {
  S,
  money,
  intFmt,
  pctFmt,
  Kpi,
  SectionCard,
  Cargando,
  ErrorMsg,
  buildQuery,
  descargarCSV,
} from './reportesCommon';

const TIPO_META = {
  tienda: { label: 'Tienda', color: COL.pri2 },
  online: { label: 'Online', color: COL.purple },
};

export default function ReporteVentas({ filtros }) {
  const [resumen, setResumen] = useState(null);
  const [serie, setSerie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [tab, setTab] = useState('productos');
  const [prods, setProds] = useState([]);
  const [prodsHasMore, setProdsHasMore] = useState(false);
  const [prodsCursor, setProdsCursor] = useState(null);
  const [prodsLoading, setProdsLoading] = useState(false);
  const [prodsQ, setProdsQ] = useState('');
  const [orden, setOrden] = useState('ingresos');
  const [txs, setTxs] = useState([]);
  const [txsHasMore, setTxsHasMore] = useState(false);
  const [txsCursor, setTxsCursor] = useState(null);
  const [txsLoading, setTxsLoading] = useState(false);
  const [txsTipo, setTxsTipo] = useState('');
  const [detalle, setDetalle] = useState(null);

  const baseParams = () => buildQuery(filtros);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    setResumen(null);
    setSerie([]);
    Promise.all([
      API.get(`/ventas/reporte/resumen?${baseParams().toString()}`),
      API.get(`/reportes/ventas/serie?${buildQuery(filtros).toString()}`),
    ])
      .then(([r1, r2]) => {
        if (!alive) return;
        if (r1.data.status === 'success') setResumen(r1.data.data);
        if (r2.data.status === 'success') setSerie(r2.data.data.serie || []);
      })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.sede, filtros.fechaDesde, filtros.fechaHasta, filtros.departamento]);

  useEffect(() => {
    setProds([]);
    setProdsCursor(null);
    setProdsHasMore(false);
    cargarProds(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.sede, filtros.fechaDesde, filtros.fechaHasta, filtros.departamento, prodsQ, orden]);

  useEffect(() => {
    if (tab !== 'transacciones') return;
    setTxs([]);
    setTxsCursor(null);
    setTxsHasMore(false);
    cargarTxs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filtros.sede, filtros.fechaDesde, filtros.fechaHasta, txsTipo]);

  const cargarProds = async (reset) => {
    setProdsLoading(true);
    try {
      const p = baseParams();
      p.set('limite', '50');
      p.set('orden', orden);
      if (prodsQ.trim()) p.set('q', prodsQ.trim());
      if (!reset && prodsCursor) {
        p.set('ultimo_valor', prodsCursor.valor);
        p.set('ultimo_codigo', prodsCursor.codigo);
      }
      const r = await API.get(`/ventas/reporte/productos?${p.toString()}`);
      if (r.data.status === 'success') {
        const d = r.data.data;
        setProds((prev) => (reset ? d.productos : [...prev, ...d.productos]));
        setProdsHasMore(d.has_more);
        if (d.has_more) setProdsCursor({ valor: d.ultimo_valor, codigo: d.ultimo_codigo });
      }
    } catch {
    } finally {
      setProdsLoading(false);
    }
  };

  const cargarTxs = async (reset) => {
    setTxsLoading(true);
    try {
      const p = baseParams();
      p.set('limite', '50');
      if (txsTipo) p.set('tipo', txsTipo);
      if (!reset && txsCursor) {
        p.set('ultimo_fecha', txsCursor.fecha);
        p.set('ultimo_sort_id', txsCursor.sortId);
      }
      const r = await API.get(`/ventas/reporte/transacciones?${p.toString()}`);
      if (r.data.status === 'success') {
        const d = r.data.data;
        setTxs((prev) => (reset ? d.transacciones : [...prev, ...d.transacciones]));
        setTxsHasMore(d.has_more);
        if (d.has_more) setTxsCursor({ fecha: d.ultimo_fecha, sortId: d.ultimo_sort_id });
      }
    } catch {
    } finally {
      setTxsLoading(false);
    }
  };

  const abrirDetalle = async (tipo, id) => {
    try {
      const r = await API.get(`/ventas/reporte/transaccion?tipo=${tipo}&id=${id}`);
      if (r.data.status === 'success') setDetalle(r.data.data);
    } catch {}
  };

  const exportarCSV = () => {
    if (tab === 'productos') {
      descargarCSV(
        `reporte_ventas_productos.csv`,
        ['Producto', 'Código', 'Unidades', 'Ingresos', 'Costo', 'Margen', 'Margen %', 'Nº ventas'],
        prods.map((p) => [
          p.nombre, p.codigo, p.unidades, p.ingresos.toFixed(2), p.costo.toFixed(2),
          p.margen.toFixed(2), `${p.margen_pct}%`, p.transacciones,
        ])
      );
    } else {
      descargarCSV(
        `reporte_ventas_transacciones.csv`,
        ['Fecha', 'Tipo', 'Sede', 'Método', 'Items', 'Total', 'Cliente', 'Cajero'],
        txs.map((t) => [
          t.fecha, (TIPO_META[t.tipo]?.label || t.tipo), t.sede_codigo, t.metodo_pago,
          t.total_items, t.total.toFixed(2), t.cliente_nombre || '', t.cajero_nombre || '',
        ])
      );
    }
  };

  const exportarExcel = async () => {
    try {
      const r = await API.get(`/ventas/reporte/exportar?${baseParams().toString()}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_ventas.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {}
  };

  if (loading) return <Cargando />;
  if (error) return <ErrorMsg />;

  const cmp = resumen?.comparativa || null;
  const perdida = resumen?.perdida || null;

  const serieChart = serie.length > 0 && (
    <SectionCard title="Ingresos por día" icon={FiBarChart2} color={COL.pri2}>
      <LineChart
        series={[
          { name: 'Ingresos', values: serie.map((d) => d.ingresos), color: COL.pri2, area: true },
        ]}
        labels={serie.map((d) => d.dia.slice(5))}
        fmt={money}
      />
    </SectionCard>
  );

  const alertaPerdida = perdida && perdida.productos > 0 && (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        background: 'var(--agx-red-bg)',
        color: COL.red,
        fontSize: '12px',
        fontWeight: 700,
        borderRadius: '8px',
        padding: '8px 10px',
        marginBottom: '12px',
      }}
    >
      <FiAlertTriangle size={14} />
      {perdida.productos} producto{perdida.productos !== 1 ? 's' : ''} con pérdida ({money(perdida.monto)})
    </div>
  );

  return (
    <div>
      <div style={S.kpiGrid}>
        <Kpi label="Ingresos" value={money(resumen?.ingresos)} color={COL.pri}
          delta={cmp ? { actual: resumen.ingresos, anterior: cmp.ingresos } : null} />
        <Kpi label="Unidades" value={intFmt(resumen?.unidades)} />
        <Kpi label="Transacciones" value={intFmt(resumen?.transacciones)} color={COL.pri}
          delta={cmp ? { actual: resumen.transacciones, anterior: cmp.transacciones } : null} />
        <Kpi label="Margen" value={money(resumen?.margen)} sub={pctFmt(resumen?.margen_pct)}
          color={resumen?.margen > 0 ? COL.green : COL.red} />
        <Kpi label="Costo" value={money(resumen?.costo)} />
        <Kpi label="Ticket promedio" value={money(resumen?.ticket_promedio)} />
      </div>

      {alertaPerdida}
      {serieChart}

      {(resumen?.por_departamento || []).length > 0 && (
        <SectionCard title="Por departamento" icon={FiBarChart2} color={COL.pri2}>
          <BarsH
            items={(resumen.por_departamento || []).map((d) => ({
              label: d.grupo,
              value: d.ingresos,
              sub: `${intFmt(d.unidades)} unds · margen ${money(d.margen)}`,
            }))}
            fmt={money}
            color={COL.pri2}
          />
        </SectionCard>
      )}

      <SectionCard
        title="Reporte de ventas"
        icon={FiBarChart2}
        color={COL.pri}
        hint="Ventas por pedido (tienda y online). Detalle por producto y por transacción."
      >
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <button onClick={() => setTab('productos')} style={{ ...S.btn, ...(tab === 'productos' ? S.btnActive : {}) }}>
            <FiList size={12} style={{ verticalAlign: '-2px' }} /> Productos
          </button>
          <button onClick={() => setTab('transacciones')} style={{ ...S.btn, ...(tab === 'transacciones' ? S.btnActive : {}) }}>
            <FiClock size={12} style={{ verticalAlign: '-2px' }} /> Transacciones
          </button>
          <div style={{ flex: 1 }} />
          <button onClick={exportarCSV} style={S.btn}>
            <FiDownload size={12} style={{ verticalAlign: '-2px' }} /> CSV
          </button>
          <button onClick={exportarExcel} style={S.btn}>
            <FiDownload size={12} style={{ verticalAlign: '-2px' }} /> Excel
          </button>
        </div>

        {tab === 'productos' ? (
          <>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <div style={{ position: 'relative', flex: '1', minWidth: '180px' }}>
                <FiSearch size={13} color={COL.muted}
                  style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)' }} />
                <input value={prodsQ} onChange={(e) => setProdsQ(e.target.value)}
                  placeholder="Buscar producto o código…"
                  style={{ ...S.input, width: '100%', paddingLeft: '28px', boxSizing: 'border-box' }} />
              </div>
              <select value={orden} onChange={(e) => setOrden(e.target.value)} style={S.select}>
                <option value="ingresos">Ordenar: Ingresos</option>
                <option value="unidades">Ordenar: Unidades</option>
                <option value="margen">Ordenar: Margen</option>
              </select>
            </div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {['Producto', 'Unidades', 'Ingresos', 'Costo', 'Margen', 'Margen %', 'Nº ventas', 'Última venta'].map((h) => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {prods.map((p, i) => (
                    <tr key={`${p.codigo}-${i}`} style={p.margen < 0 ? { background: 'var(--agx-red-bg)' } : undefined}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                        <div style={{ color: COL.muted, fontSize: '10px' }}>{p.codigo}</div>
                      </td>
                      <td style={S.td}>{intFmt(p.unidades)}</td>
                      <td style={{ ...S.td, fontWeight: 600, color: COL.green }}>{money(p.ingresos)}</td>
                      <td style={{ ...S.td, color: COL.muted }}>{money(p.costo)}</td>
                      <td style={{ ...S.td, color: p.margen > 0 ? COL.green : COL.red, fontWeight: 600 }}>{money(p.margen)}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: p.margen > 0 ? COL.green : COL.red }}>{pctFmt(p.margen_pct)}</td>
                      <td style={S.td}>{intFmt(p.transacciones)}</td>
                      <td style={{ ...S.td, color: COL.muted, fontSize: '11px' }}>{p.ultima ? p.ultima.slice(0, 10) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {prods.length === 0 && !prodsLoading && <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin ventas en el período</div>}
            {prodsHasMore && (
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <button onClick={() => cargarProds(false)} disabled={prodsLoading} style={S.btn}>
                  {prodsLoading ? 'Cargando…' : 'Cargar más'}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <select value={txsTipo} onChange={(e) => setTxsTipo(e.target.value)} style={S.select}>
                <option value="">Todos los tipos</option>
                <option value="tienda">Tienda</option>
                <option value="online">Online</option>
              </select>
            </div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {['Fecha', 'Tipo', 'Sede', 'Método', 'Items', 'Total', 'Cliente / Cajero'].map((h) => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {txs.map((t) => (
                    <tr key={`${t.tipo}-${t.sort_id}`} onClick={() => abrirDetalle(t.tipo, t.id)} style={{ cursor: 'pointer' }}>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                        <div>{t.fecha.slice(0, 10)}</div>
                        <div style={{ color: COL.muted, fontSize: '10px' }}>{t.fecha.slice(11, 16)}</div>
                      </td>
                      <td style={S.td}>
                        <span style={{ ...S.chip, color: (TIPO_META[t.tipo] || {}).color || COL.muted, background: COL.bg }}>
                          {(TIPO_META[t.tipo] || {}).label || t.tipo}
                        </span>
                      </td>
                      <td style={{ ...S.td, color: COL.muted }}>{t.sede_codigo}</td>
                      <td style={{ ...S.td, color: COL.muted, textTransform: 'capitalize' }}>{(t.metodo_pago || '—').replace(/_/g, ' ')}</td>
                      <td style={S.td}>{intFmt(t.total_items)}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: COL.green }}>{money(t.total)}</td>
                      <td style={{ ...S.td, color: COL.muted }}>
                        <div>{t.cliente_nombre || '—'}</div>
                        <div style={{ fontSize: '10px' }}>{t.cajero_nombre ? `· ${t.cajero_nombre}` : ''}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {txs.length === 0 && !txsLoading && <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin transacciones en el período</div>}
            {txsHasMore && (
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <button onClick={() => cargarTxs(false)} disabled={txsLoading} style={S.btn}>
                  {txsLoading ? 'Cargando…' : 'Cargar más'}
                </button>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {detalle && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}
          onClick={() => setDetalle(null)}>
          <div style={{ background: COL.white, borderRadius: '12px', maxWidth: '560px', width: '100%', maxHeight: '80vh', overflowY: 'auto', padding: '16px', boxShadow: '0 12px 32px rgba(16,24,40,0.25)' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
              <div style={{ fontSize: '15px', fontWeight: 800, color: COL.pri }}>Detalle de transacción</div>
              <button onClick={() => setDetalle(null)} style={{ ...S.btn, padding: '4px 8px' }}>Cerrar</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '12px', color: COL.muted }}>
              <span style={{ fontWeight: 700, color: COL.pri }}>#{detalle.cabecera.id} · {money(detalle.cabecera.total)}</span>
              <span>{(detalle.cabecera.metodo_pago || '—').replace(/_/g, ' ')}</span>
              <span>{detalle.cabecera.sede_codigo}</span>
              <span>{detalle.cabecera.fecha?.slice(0, 16).replace('T', ' ')}</span>
            </div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>{['Producto', 'Cantidad', 'Ingresos', 'Costo', 'Margen'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {detalle.items.map((it, i) => (
                    <tr key={i}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600 }}>{it.nombre}</div>
                        <div style={{ color: COL.muted, fontSize: '10px' }}>{it.codigo}</div>
                      </td>
                      <td style={S.td}>{intFmt(it.cantidad)}</td>
                      <td style={{ ...S.td, color: COL.green }}>{money(it.subtotal)}</td>
                      <td style={{ ...S.td, color: COL.muted }}>{money(it.costo)}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: it.margen > 0 ? COL.green : COL.red }}>{money(it.margen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
